import { useMemo, useState } from 'react'
import { Card, Empty, InputNumber, Select, Typography } from '@arco-design/web-react'
import { formatUsdAsDisplay } from '@/utils/quota'
import {
  describeEffectivePricing,
  estimateQuota,
  type EffectivePricing,
  type ModelPricingEntry,
} from '@/api/modelPricing'

interface Props {
  entries: Record<string, ModelPricingEntry>
  /** 当前正在编辑的模型，便于草稿价即时试算 */
  defaultModel?: string
}

const fmtUsd = (v: number): string => (Number.isFinite(v) ? formatUsdAsDisplay(v, 6) : '-')

/**
 * 定价试算：输入 token 用量，按「实际生效价」算出会扣多少钱。
 * 只做本地计算（公式与后端一致），不改任何配置。
 */
export default function PricingSimulator({ entries, defaultModel }: Props) {
  const modelNames = useMemo(
    () => Object.keys(entries).sort((a, b) => a.localeCompare(b)),
    [entries]
  )
  const [model, setModel] = useState(defaultModel || modelNames[0] || '')
  const [promptTokens, setPromptTokens] = useState(1000)
  const [completionTokens, setCompletionTokens] = useState(500)
  const [cacheReadTokens, setCacheReadTokens] = useState(0)
  const [cacheWriteTokens, setCacheWriteTokens] = useState(0)
  const [groupRatio, setGroupRatio] = useState(1)

  const effectiveModel = model || defaultModel || modelNames[0] || ''
  const entry = entries[effectiveModel]

  const pricing: EffectivePricing | null = useMemo(() => {
    if (!entry) return null
    return describeEffectivePricing(entry.effective, entry.configured)
  }, [entry])

  const result = useMemo(() => {
    if (!pricing) return null
    return estimateQuota(
      pricing,
      { promptTokens, completionTokens, cacheReadTokens, cacheWriteTokens },
      groupRatio
    )
  }, [pricing, promptTokens, completionTokens, cacheReadTokens, cacheWriteTokens, groupRatio])

  if (!modelNames.length) {
    return (
      <Card title="定价试算" className="!rounded-2xl">
        <Empty description="还没有任何模型定价，先在左侧表格里配置" />
      </Card>
    )
  }

  return (
    <Card
      title="定价试算"
      className="!rounded-2xl"
      extra={
        <Typography.Text className="text-xs text-[var(--console-text-muted)]">
          按「实际生效价」本地计算，不改配置
        </Typography.Text>
      }
    >
      <div className="grid gap-3 md:grid-cols-2">
        <div className="space-y-2">
          <div className="text-xs text-[var(--console-text-muted)]">模型</div>
          <Select
            showSearch
            value={effectiveModel}
            onChange={setModel}
            options={modelNames.map((name) => ({ value: name, label: name }))}
            style={{ width: '100%' }}
          />
          <div className="grid grid-cols-2 gap-2 pt-1">
            <label className="text-xs">
              <div className="mb-1 text-[var(--console-text-muted)]">输入 tokens</div>
              <InputNumber
                min={0}
                value={promptTokens}
                onChange={(v) => setPromptTokens(Number(v ?? 0))}
                style={{ width: '100%' }}
              />
            </label>
            <label className="text-xs">
              <div className="mb-1 text-[var(--console-text-muted)]">输出 tokens</div>
              <InputNumber
                min={0}
                value={completionTokens}
                onChange={(v) => setCompletionTokens(Number(v ?? 0))}
                style={{ width: '100%' }}
              />
            </label>
            <label className="text-xs">
              <div className="mb-1 text-[var(--console-text-muted)]">缓存读 tokens</div>
              <InputNumber
                min={0}
                value={cacheReadTokens}
                onChange={(v) => setCacheReadTokens(Number(v ?? 0))}
                style={{ width: '100%' }}
              />
            </label>
            <label className="text-xs">
              <div className="mb-1 text-[var(--console-text-muted)]">缓存写 tokens</div>
              <InputNumber
                min={0}
                value={cacheWriteTokens}
                onChange={(v) => setCacheWriteTokens(Number(v ?? 0))}
                style={{ width: '100%' }}
              />
            </label>
            <label className="text-xs md:col-span-2">
              <div className="mb-1 text-[var(--console-text-muted)]">
                分组倍率（客户所在分组的倍率，默认 1）
              </div>
              <InputNumber
                min={0}
                step={0.1}
                value={groupRatio}
                onChange={(v) => setGroupRatio(Number(v ?? 1))}
                style={{ width: '100%' }}
              />
            </label>
          </div>
        </div>

        <div className="space-y-2 rounded-xl border border-[var(--console-border)] p-3">
          <div className="text-xs text-[var(--console-text-muted)]">这笔请求大约扣</div>
          <div className="text-2xl font-semibold tabular-nums">
            {result ? fmtUsd(result.usd) : '-'}
          </div>
          <div className="text-xs tabular-nums text-[var(--console-text-muted)]">
            = {result ? result.quota.toLocaleString() : '-'} quota
          </div>
          {pricing ? (
            <div className="space-y-1 pt-2 text-xs text-[var(--console-text-muted)]">
              {pricing.fixedPrice ? (
                <div>按次固定价 ${pricing.fixedPrice}</div>
              ) : (
                <>
                  <div>输入 ${pricing.inputPerMillion ?? '-'} / 1M</div>
                  <div>输出 ${pricing.outputPerMillion ?? '-'} / 1M</div>
                </>
              )}
              {pricing.defaultedKeys.length ? (
                <div className="text-amber-600">
                  其中 {pricing.defaultedKeys.join('、')} 用的是引擎默认值
                </div>
              ) : null}
            </div>
          ) : (
            <div className="pt-2 text-xs text-[var(--console-text-muted)]">
              该模型还没有生效定价，请先在表格里配置
            </div>
          )}
        </div>
      </div>
    </Card>
  )
}
